export const colorDifference = (color1, color2) => {
  const rgb1 = colorToRGBObject(color1);
  const rgb2 = colorToRGBObject(color2);

  if (rgb1 && rgb2) {
    const rDiff = rgb1.r - rgb2.r;
    const gDiff = rgb1.g - rgb2.g;
    const bDiff = rgb1.b - rgb2.b;

    return rDiff * rDiff + gDiff * gDiff + bDiff * bDiff;
  }

  return Infinity;
};

const colorToRGBObject = (color) => {
  if (typeof color === "string") {
    const rgbString = colorToRGB(color);
    if (rgbString) {
      const rgbMatch = rgbString.match(
        /^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i,
      );
      if (rgbMatch) {
        return {
          r: parseInt(rgbMatch[1], 10),
          g: parseInt(rgbMatch[2], 10),
          b: parseInt(rgbMatch[3], 10),
        };
      }
    }
  }

  return null;
};

export const colorToRGB = (colorVal) => {
  const isValidText = colorVal && colorVal.trim && colorVal.toLowerCase;
  const colorText = isValidText ? colorVal.trim().toLowerCase() : "";

  if (!colorText) {
    return null;
  } else if (colorText.startsWith("rgb")) {
    // rgb
    return rgbTextToRGB(colorText);
  } else if (colorText.startsWith("#")) {
    // hex
    return hexTextToRGB(colorText);
  } else if (colorText.startsWith("hsl")) {
    // hsl
    return hslToRGB(colorText);
  } else {
    // color name
    return colorNameToRGB(colorText);
  }
};

export const getFillOpacity = (colorVal) => {
  const isValidText = colorVal && colorVal.trim && colorVal.toLowerCase;
  const colorText = isValidText ? colorVal.trim().toLowerCase() : "";

  if (!colorText) {
    return null;
  } else if (colorText.startsWith("rgb")) {
    // rgb
    const [_r, _g, _b, a] = rgbTextToRGBArray(colorText);
    return a === undefined ? null : Number(a);
  } else {
    // hex & color name
    return 1;
  }
};

const rgbTextToRGBArray = (text) => {
  if (!text) {
    return [];
  }
  const fillColorRegex = /[\d+|.|\s]+/g;
  const match = text.match(fillColorRegex);
  return Array.isArray(match) ? match : [];
};

const rgbTextToRGB = (text) => {
  if (!text) {
    return null;
  }
  const rgbArray = rgbTextToRGBArray(text);
  const [r, g, b] = rgbArray.length ? rgbArray : [];
  return rgbArray.length ? `rgb(${r}, ${g}, ${b})` : null;
};

// @ref: https://stackoverflow.com/questions/5623838/rgb-to-hex-and-hex-to-rgb
const hexTextToRGB = (text) => {
  // @ref: https://stackoverflow.com/questions/8027423/how-to-check-if-a-string-is-a-valid-hex-color-representation
  const reg = /^#([0-9a-f]{3}){1,2}$/i;
  const isValidHex = text && reg.test(text);
  if (!isValidHex) {
    return null;
  }
  // Expand shorthand form (e.g. "03F") to full form (e.g. "0033FF")
  const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
  const hex = text.replace(shorthandRegex, function (m, r, g, b) {
    return r + r + g + g + b + b;
  });

  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  const colorDict = result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : "";
  return colorDict
    ? `rgb(${colorDict.r}, ${colorDict.g}, ${colorDict.b})`
    : null;
};

// @ref: https://stackoverflow.com/a/47355187
const colorNameToRGB = (text) => {
  if (!text || !text.toLowerCase) {
    return null;
  }
  const textColor = text.toLowerCase();
  const ctx = document.createElement("canvas").getContext("2d");
  ctx.fillStyle = textColor;
  const nonValidColor = textColor !== "black" && ctx.fillStyle === "#000000";
  return nonValidColor ? null : hexTextToRGB(ctx.fillStyle);
};

// @ref: https://css-tricks.com/converting-color-spaces-in-javascript/
const hslToRGB = (text) => {
  let sep = text.indexOf(",") > -1 ? "," : " ";
  let hsl = text.substr(4).split(")")[0].split(sep);

  let h = hsl[0],
    s = hsl[1].substr(0, hsl[1].length - 1) / 100,
    l = hsl[2].substr(0, hsl[2].length - 1) / 100;

  let c = (1 - Math.abs(2 * l - 1)) * s,
    x = c * (1 - Math.abs(((h / 60) % 2) - 1)),
    m = l - c / 2,
    r = 0,
    g = 0,
    b = 0;
  if (0 <= h && h < 60) {
    r = c;
    g = x;
    b = 0;
  } else if (60 <= h && h < 120) {
    r = x;
    g = c;
    b = 0;
  } else if (120 <= h && h < 180) {
    r = 0;
    g = c;
    b = x;
  } else if (180 <= h && h < 240) {
    r = 0;
    g = x;
    b = c;
  } else if (240 <= h && h < 300) {
    r = x;
    g = 0;
    b = c;
  } else if (300 <= h && h < 360) {
    r = c;
    g = 0;
    b = x;
  }
  r = Math.round((r + m) * 255);
  g = Math.round((g + m) * 255);
  b = Math.round((b + m) * 255);

  return "rgb(" + r + "," + g + "," + b + ")";
};
